/**
 * Discount Manager Module
 * Handles discount code validation and application in checkout
 */

import { debounce, formatPrice, getCartType } from '../core/utils.js';
import { getStorage } from '../core/storage.js';
import { t } from '../core/i18n.js';

export class DiscountManager {
    constructor(system) {
        this.system = system;
        this.config = {
            apiUrl: '/cart/api/v1/discount',
            debounceDelay: 500,
            merchantId: system.config.merchantId || null,
            customerId: system.config.customerId || null,
            ...system.config.discount
        };
        
        this.currentDiscount = null;
        this.automaticDiscounts = [];
        this.manualDiscount = null;
        this.discountStack = [];
        this.isValidating = false;
        this.elements = {};
        
        // Debounced validation function
        this.debouncedValidate = debounce(
            this.validateDiscountCode.bind(this), 
            this.config.debounceDelay
        );
        
        console.log('DiscountManager: Initialized with config:', this.config);
    }

    init() {
        console.log('DiscountManager: Initializing discount functionality');
        
        this.findElements();
        
        // Check cart type - if only gift cards, hide discount section entirely
        const cartItems = getStorage(this.system.config.storageKey) || [];
        const cartType = getCartType(cartItems);
        
        console.log('DiscountManager: Cart type detected:', cartType);
        
        if (cartType === 'only-giftcards') {
            console.log('DiscountManager: Only gift cards in cart - hiding discount section');
            if (this.elements.discountSection) {
                this.elements.discountSection.style.display = 'none';
            }
            return; // Exit early - no discount functionality for gift cards only
        }
        
        this.bindEvents();
        this.setupShippingListeners(); // NEW: Listen for shipping changes
        this.updateUI();
        
        // Check for automatic discounts when page loads
        setTimeout(() => {
            this.checkAutomaticDiscountsOnLoad();
        }, 500); // Wait for other systems to initialize
    }

    findElements() {
        // Find discount-related elements in the DOM
        this.elements = {
            discountSection: document.getElementById('discount-section'),
            discountInput: document.getElementById('discount-code'),
            applyButton: document.getElementById('apply-discount'),
            removeButton: document.getElementById('remove-discount'),
            discountMessage: document.getElementById('discount-message'),
            discountRow: document.getElementById('order-discount-row'),
            discountAmount: document.getElementById('order-discount-amount'),
            orderTotal: document.getElementById('order-total-amount'),
            orderSubtotal: document.getElementById('order-subtotal-amount')
        };
        
        // Debug: Log which elements were found
        console.log('DiscountManager: Found elements:', {
            discountSection: !!this.elements.discountSection,
            discountInput: !!this.elements.discountInput,
            applyButton: !!this.elements.applyButton,
            removeButton: !!this.elements.removeButton,
            discountMessage: !!this.elements.discountMessage,
            discountRow: !!this.elements.discountRow,
            discountAmount: !!this.elements.discountAmount,
            orderTotal: !!this.elements.orderTotal,
            orderSubtotal: !!this.elements.orderSubtotal
        });
        
        // Warn if critical elements are missing
        if (!this.elements.discountInput || !this.elements.applyButton) {
            console.warn('DiscountManager: Critical elements missing - discount functionality may not work');
        }
    }

    bindEvents() {
        const { discountInput, applyButton, removeButton } = this.elements;
        
        if (discountInput) {
            // Handle Enter key
            discountInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    this.applyDiscount();
                }
            });
        }
        
        if (applyButton) {
            applyButton.addEventListener('click', () => {
                console.log('DiscountManager: Apply button clicked');
                this.applyDiscount();
            });
        }
        
        if (removeButton) {
            removeButton.addEventListener('click', () => {
                this.removeDiscount();
            });
        }
    }

    /**
     * Lyssnar på ändringar i leveransalternativ för att uppdatera automatiska rabatter
     */
    setupShippingListeners() {
        console.log('DiscountManager: Setting up shipping change listeners');
        
        // Listen for shipping option changes
        document.addEventListener('shippingUpdated', (event) => {
            console.log('DiscountManager: Shipping updated event received:', event.detail);
            this.updateAutomaticDiscounts();
        });
        
        document.addEventListener('shippingOptionChanged', (event) => {
            console.log('DiscountManager: Shipping option changed event received:', event.detail);
            this.updateAutomaticDiscounts();
        });
    }

    async validateDiscountCode(code) {
        if (this.isValidating || !code) return;
        
        console.log('DiscountManager: Validating discount code:', code);
        
        this.isValidating = true;
        this.showValidating();
        
        try {
            // Get current cart data for validation
            const cartData = this.getCartValidationData();
            
            // Get merchantId from system config
            const merchantId = this.system.config.checkout?.merchantId || this.config.merchantId;
            if (!merchantId) {
                throw new Error('MerchantId saknas i systemkonfiguration');
            }
            
            // Get customer email from checkout form if available
            const customerEmail = this.getCustomerEmail();
            
            const requestData = {
                code: code,
                merchantId: merchantId,
                customerId: this.config.customerId || this.system.config.customerId || null,
                customerEmail: customerEmail,
                orderAmount: cartData.totalAmount,
                productCategoryIds: cartData.categoryIds
            };
            
            console.log('DiscountManager: Sending validation request:', requestData);
            
            const response = await fetch(`${this.config.apiUrl}/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });
            
            console.log('DiscountManager: API response status:', response.status);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('DiscountManager: API error response:', errorText);
                throw new Error(`API error: ${response.status} - ${errorText}`);
            }
            
            const result = await response.json();
            console.log('DiscountManager: API response data:', result);
            
            if (result.isValid) {
                this.currentDiscount = {
                    code: code,
                    discount: result.discount,
                    discountAmount: result.discountAmount,
                    description: result.description
                };
                console.log('DiscountManager: Discount applied successfully:', this.currentDiscount);
                this.showValidDiscount(result);
            } else {
                this.currentDiscount = null;
                console.log('DiscountManager: Invalid discount code:', result.errorMessage);
                this.showInvalidDiscount(result.errorMessage);
            }
            
        } catch (error) {
            console.error('DiscountManager: Validation error:', error);
            this.showError(t('discount.error'));
            this.currentDiscount = null;
        } finally {
            this.isValidating = false;
            this.updateOrderSummary();
        }
    }

    getCartValidationData() {
        // Get cart items directly from storage using the imported getStorage function
        const cartItems = getStorage(this.system.config.storageKey) || [];
        
        console.log('DiscountManager: Cart items from storage:', cartItems);
        
        let totalAmount = 0;
        const categoryIds = new Set();
        const brandIds = new Set();
        const categoryQuantities = {};
        const productPrices = [];
        
        cartItems.forEach(item => {
            // EXCLUDE GiftCards from discount calculation to prevent abuse loop
            if (item.isGiftCard) {
                console.log('DiscountManager: Excluding GiftCard from discount calculation:', item.ProductName);
                return; // Skip this item
            }
            
            const price = parseFloat(item.ProductPrice) || 0;
            const count = parseInt(item.OrderCount) || 1;
            totalAmount += price * count;
            
            if (item.CategoryId) {
                categoryIds.add(item.CategoryId);
                
                // Track quantity per category for BuyXGetYFree discounts
                if (!categoryQuantities[item.CategoryId]) {
                    categoryQuantities[item.CategoryId] = 0;
                }
                categoryQuantities[item.CategoryId] += count;
            }
            
            if (item.BrandId) {
                brandIds.add(item.BrandId);
            }
            
            // Collect individual product prices for accurate BuyXGetYFree calculation
            // IMPORTANT: Include ProductId and ProductVariantId so backend can look up actual product/variant from database
            productPrices.push({
                productId: item.ProductId || '',
                productVariantId: item.ProductVariantId || null,
                price: price,
                quantity: count,
                productNr: item.ProductNr || '',      // DEPRECATED - kept for backward compatibility
                categoryId: item.CategoryId || null,  // DEPRECATED - kept for backward compatibility
                brandId: item.BrandId || null         // DEPRECATED - kept for backward compatibility
            });
        });
        
        console.log('DiscountManager: Calculated cart data (excluding GiftCards):', {
            totalAmount,
            categoryIds: Array.from(categoryIds),
            brandIds: Array.from(brandIds),
            categoryQuantities,
            productPrices
        });
        
        return {
            totalAmount: totalAmount,
            categoryIds: Array.from(categoryIds),
            brandIds: Array.from(brandIds),
            categoryQuantities: categoryQuantities,
            productPrices: productPrices
        };
    }

    /**
     * Beräknar totalsumman inklusive GiftCards för fraktgräns-beräkning
     * GiftCards ska räknas in i totalsumman för att kvalificera för gratis frakt
     */
    getTotalAmountForShipping() {
        const cartItems = getStorage(this.system.config.storageKey) || [];
        
        let totalAmount = 0;
        
        cartItems.forEach(item => {
            // INCLUDE GiftCards in shipping threshold calculation
            const price = parseFloat(item.ProductPrice) || 0;
            const count = parseInt(item.OrderCount) || 1;
            totalAmount += price * count;
        });
        
        console.log('DiscountManager: Total amount for shipping threshold (including GiftCards):', totalAmount);
        
        return totalAmount;
    }

    getCustomerEmail() {
        // Try to get customer email from checkout form
        const emailInput = document.getElementById('customerEmail');
        if (emailInput && emailInput.value) {
            console.log('DiscountManager: Found customer email:', emailInput.value);
            return emailInput.value.trim();
        }
        
        // Fallback: check if there's a logged-in user email in system config
        const userEmail = this.system.config.userEmail || this.config.userEmail;
        if (userEmail) {
            console.log('DiscountManager: Using system user email:', userEmail);
            return userEmail;
        }
        
        console.log('DiscountManager: No customer email found');
        return null;
    }

    applyDiscount() {
        const code = this.elements.discountInput?.value?.trim();
        if (!code) {
            this.showError(t('discount.enterCode'));
            return;
        }
        
        if (this.manualDiscount && this.manualDiscount.code === code) {
            // Already applied
            this.showSuccess(t('discount.alreadyApplied'));
            return;
        }
        
        // Use new multi-discount system
        this.validateAndApplyManualDiscount(code);
    }

    removeDiscount() {
        // Use new multi-discount system - only remove manual discounts
        if (this.manualDiscount) {
            this.removeManualDiscount();
        } else {
            this.showMessage(t('discount.noManualDiscount'), 'info');
        }
    }

    clearDiscount() {
        if (this.currentDiscount) {
            this.currentDiscount = null;
            this.clearMessages();
            this.updateOrderSummary();
            this.updateUI();
        }
    }

    showValidating() {
        this.showMessage(t('discount.validating'), 'info');
        this.setButtonState('validating');
    }

    showValidDiscount(result) {
        const message = `Rabatt applicerad: ${result.description} (-${formatPrice(result.discountAmount)})`;
        this.showMessage(message, 'success');
        this.setButtonState('applied');
    }

    showInvalidDiscount(errorMessage) {
        this.showMessage(errorMessage || t('discount.invalid'), 'error');
        this.setButtonState('error');
    }

    showError(message) {
        this.showMessage(message, 'error');
        this.setButtonState('error');
    }

    showSuccess(message) {
        this.showMessage(message, 'success');
    }

    showMessage(message, type) {
        const messageEl = this.elements.discountMessage;
        if (!messageEl) return;
        
        messageEl.textContent = message;
        messageEl.className = `discount-message ${type}`;
        messageEl.style.display = 'block';
        
        // Auto-hide success messages
        if (type === 'success') {
            setTimeout(() => {
                if (messageEl.textContent === message) {
                    messageEl.style.display = 'none';
                }
            }, 3000);
        }
    }

    clearMessages() {
        const messageEl = this.elements.discountMessage;
        if (messageEl) {
            messageEl.style.display = 'none';
            messageEl.textContent = '';
        }
    }

    setButtonState(state) {
        const { applyButton, removeButton, discountInput } = this.elements;
        
        if (applyButton) {
            switch (state) {
                case 'validating':
                    applyButton.disabled = true;
                    applyButton.textContent = t('discount.validating');
                    applyButton.style.display = 'inline-block';
                    break;
                case 'applied':
                    // Hide "Applicerad" button when "Ta bort" button is shown
                    applyButton.style.display = 'none';
                    break;
                case 'error':
                    applyButton.disabled = false;
                    applyButton.textContent = t('discount.apply');
                    applyButton.style.display = 'inline-block';
                    break;
                default:
                    applyButton.disabled = false;
                    applyButton.textContent = t('discount.apply');
                    applyButton.style.display = 'inline-block';
            }
        }
        
        if (removeButton) {
            removeButton.style.display = (state === 'applied') ? 'inline-block' : 'none';
        }
        
        if (discountInput) {
            // Only disable input during validation, never when discounts are applied
            discountInput.disabled = (state === 'validating');
        }
    }

    async updateOrderSummary() {
        const { orderTotal, orderSubtotal } = this.elements;
        
        if (!orderTotal) return;
        
        // Prevent recursive calls
        if (this.isUpdatingOrderSummary) {
            console.log('DiscountManager: Preventing recursive updateOrderSummary call');
            return;
        }
        
        this.isUpdatingOrderSummary = true;
        
        try {
            // Render separate discount rows instead of single combined row
            this.renderSeparateDiscountRows();
            
            // Use central total calculation instead of doing it ourselves
            this.requestCentralTotalCalculation();
            
            // Dispatch event for other modules (but prevent loops)
            if (!this.isDispatchingEvent) {
                this.dispatchDiscountUpdatedEvent();
            }
            
        } finally {
            this.isUpdatingOrderSummary = false;
        }
    }

    /**
     * Begär en central total-beräkning som tar hänsyn till både frakt och rabatter
     */
    requestCentralTotalCalculation() {
        const event = new CustomEvent('requestTotalUpdate', {
            detail: {
                source: 'discount',
                totalDiscountAmount: this.getTotalDiscountAmount(),
                discountStack: this.discountStack
            }
        });
        document.dispatchEvent(event);
    }

    updateUI() {
        // Only disable button if we have a MANUAL discount applied
        const hasManualDiscount = this.manualDiscount !== null;
        const hasAutomaticDiscount = this.automaticDiscounts.length > 0;
        
        // Set button state based on manual discount only
        this.setButtonState(hasManualDiscount ? 'applied' : 'default');
        
        // Extra safety: Ensure input is always enabled when only automatic discounts are present
        if (hasAutomaticDiscount && !hasManualDiscount && this.elements.discountInput) {
            this.elements.discountInput.disabled = false;
        }
        
        // Add classes for styling
        if (hasManualDiscount || hasAutomaticDiscount) {
            this.elements.discountSection?.classList.add('has-discount');
        } else {
            this.elements.discountSection?.classList.remove('has-discount');
        }
        
        // Add specific class for automatic discounts
        if (hasAutomaticDiscount) {
            this.elements.discountSection?.classList.add('has-automatic-discount');
        } else {
            this.elements.discountSection?.classList.remove('has-automatic-discount');
        }
    }

    parsePrice(priceText) {
        // Parse "1 234,56 kr" or "1234.56 kr" format
        const cleanText = priceText.replace(/[^\d,.-]/g, '');
        const normalizedText = cleanText.replace(',', '.');
        return parseFloat(normalizedText) || 0;
    }

    dispatchDiscountUpdatedEvent() {
        const event = new CustomEvent('discountUpdated', {
            detail: {
                discount: this.currentDiscount,
                discountAmount: this.currentDiscount?.discountAmount || 0
            }
        });
        document.dispatchEvent(event);
    }

    /**
     * Hämtar automatiska frakt-rabatter baserat på orderbelopp och leveransmetod
     */
    async fetchAutomaticShippingDiscounts(orderAmount, shippingMethod = null) {
        try {
            const merchantId = this.system.config.checkout?.merchantId || this.config.merchantId;
            if (!merchantId) {
                console.warn('DiscountManager: MerchantId saknas för automatiska frakt-rabatter');
                return [];
            }

            // Build URL with optional shippingMethod parameter
            let url = `/cart/api/v1/discount/merchant/${merchantId}/automatic-shipping?orderAmount=${orderAmount}`;
            if (shippingMethod) {
                url += `&shippingMethod=${encodeURIComponent(shippingMethod)}`;
            }

            console.log('DiscountManager: Fetching automatic shipping discounts with URL:', url);

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const discounts = await response.json();
            console.log('DiscountManager: Automatiska frakt-rabatter:', discounts);
            return discounts;

        } catch (error) {
            console.error('DiscountManager: Fel vid hämtning av automatiska frakt-rabatter:', error);
            return [];
        }
    }

    /**
     * Applicerar automatiska frakt-rabatter
     */
    async applyAutomaticShippingDiscounts() {
        // Prevent recursive calls
        if (this.isApplyingAutomaticDiscounts) {
            console.log('DiscountManager: Preventing recursive applyAutomaticShippingDiscounts call');
            return false;
        }
        
        this.isApplyingAutomaticDiscounts = true;
        
        try {
            const cartData = this.getCartValidationData();
            const selectedShippingOption = this.getSelectedShippingOption();
            const shippingMethod = selectedShippingOption?.shippingMethod || null;
            
            console.log('DiscountManager: Fetching automatic discounts for shipping method:', shippingMethod);
            
            const automaticDiscounts = await this.fetchAutomaticShippingDiscounts(
                cartData.totalAmount, 
                shippingMethod
            );
            
            if (automaticDiscounts.length > 0) {
                // Välj den första (högst prioritet) automatiska frakt-rabatten
                const discount = automaticDiscounts[0];
                
                this.currentDiscount = {
                    code: discount.code || 'AUTO_FREE_SHIPPING',
                    discount: discount,
                    discountAmount: Math.min(discount.maxShippingDiscount || 999999, this.getShippingCost()),
                    description: discount.name,
                    isAutomatic: true
                };
                
                console.log('DiscountManager: Automatisk frakt-rabatt applicerad:', this.currentDiscount);
                this.showAutomaticDiscountMessage(discount.name);
                
                return true;
            }
            
            return false;
        } finally {
            this.isApplyingAutomaticDiscounts = false;
        }
    }

    /**
     * Hämtar nuvarande fraktkostnad från ShippingOptionsManager
     */
    getShippingCost() {
        // Integration med ShippingOptionsManager
        const shippingManager = this.system.modules?.shippingOptions;
        if (shippingManager && typeof shippingManager.getSelectedShippingCost === 'function') {
            return shippingManager.getSelectedShippingCost();
        }
        
        // Fallback: försök hitta fraktkostnad i DOM
        const shippingCostElement = document.getElementById('shipping-cost-amount');
        if (shippingCostElement) {
            return this.parsePrice(shippingCostElement.textContent);
        }
        
        return 0;
    }

    /**
     * Visar meddelande för automatisk rabatt
     */
    showAutomaticDiscountMessage(discountName) {
        this.showMessage(`${discountName} har applicerats automatiskt!`, 'success');
    }

    /**
     * Kontrollerar automatiska rabatter när sidan laddas
     */
    async checkAutomaticDiscountsOnLoad() {
        console.log('DiscountManager: Checking automatic discounts on page load');
        
        // Kontrollera om vi har produkter i varukorgen
        const cartData = this.getCartValidationData();
        if (cartData.totalAmount === 0) {
            console.log('DiscountManager: No cart items found, skipping automatic discount check');
            return;
        }
        
        // Kontrollera automatiska frakt-rabatter
        await this.updateAutomaticDiscounts();
    }

    /**
     * Kontrollerar om nuvarande rabatt är automatisk
     */
    isAutomaticDiscount() {
        return this.currentDiscount?.isAutomatic === true;
    }

    /**
     * Uppdaterar automatiska rabatter (ny metod för multi-rabatt-systemet)
     */
    async updateAutomaticDiscounts() {
        console.log('DiscountManager: Updating automatic discounts');
        
        // Use getTotalAmountForShipping() to INCLUDE GiftCards in shipping threshold
        const totalAmountForShipping = this.getTotalAmountForShipping();
        console.log('DiscountManager: Total amount for shipping threshold:', totalAmountForShipping);
        
        // Get selected shipping method to filter applicable discounts
        const selectedShippingOption = this.getSelectedShippingOption();
        const shippingMethod = selectedShippingOption?.shippingMethod || null;
        
        console.log('DiscountManager: Fetching discounts for shipping method:', shippingMethod);
        
        const newAutomaticDiscounts = await this.fetchAutomaticShippingDiscounts(
            totalAmountForShipping, 
            shippingMethod
        );
        console.log('DiscountManager: Fetched automatic discounts (already filtered by backend):', newAutomaticDiscounts);
        
        if (newAutomaticDiscounts.length > 0) {
            // Hämta nuvarande leveransalternativ
            const selectedShippingOption = this.getSelectedShippingOption();
            console.log('DiscountManager: Current shipping option:', selectedShippingOption);
            
            // Filtrera rabatter baserat på leveransmetod
            const applicableDiscounts = newAutomaticDiscounts.filter(discount => {
                return this.isDiscountApplicableForShippingMethod(discount, selectedShippingOption);
            });
            
            console.log('DiscountManager: Applicable discounts after shipping method filter:', applicableDiscounts);
            
            if (applicableDiscounts.length > 0) {
                // Hämta fraktkostnad (kan vara 0 om shipping inte är valt än)
                const shippingCost = this.getShippingCost();
                console.log('DiscountManager: Current shipping cost:', shippingCost);
                
                // Uppdatera automatiska rabatter
                this.automaticDiscounts = applicableDiscounts.map(discount => {
                    // För frakt-rabatter, använd maxShippingDiscount eller fraktkostnad
                    let discountAmount = discount.maxShippingDiscount || 999999;
                    
                    // Om vi har en fraktkostnad, begränsa rabatten till den
                    if (shippingCost > 0) {
                        discountAmount = Math.min(discountAmount, shippingCost);
                    }
                    
                    console.log('DiscountManager: Processing applicable automatic discount:', {
                        name: discount.name,
                        applicableShippingMethods: discount.applicableShippingMethods,
                        maxShippingDiscount: discount.maxShippingDiscount,
                        shippingCost: shippingCost,
                        finalDiscountAmount: discountAmount
                    });
                    
                    return {
                        id: discount.id,
                        code: discount.code || 'AUTO_FREE_SHIPPING',
                        name: discount.name,
                        discountAmount: discountAmount,
                        description: discount.name,
                        isAutomatic: true,
                        priority: 1 // Automatiska rabatter har högst prioritet
                    };
                });
                
                console.log('DiscountManager: Final automatic discounts:', this.automaticDiscounts);
            } else {
                console.log('DiscountManager: No applicable discounts for current shipping method');
                this.automaticDiscounts = [];
            }
            
            // Uppdatera rabatt-stacken
            this.updateDiscountStack();
            
            // Dispatcha event för automatiska rabatter
            const event = new CustomEvent('automaticDiscountsApplied', {
                detail: {
                    discounts: this.automaticDiscounts,
                    totalAmount: this.automaticDiscounts.reduce((sum, d) => sum + d.discountAmount, 0)
                }
            });
            document.dispatchEvent(event);
        } else {
            console.log('DiscountManager: No automatic discounts found');
            this.automaticDiscounts = [];
            this.updateDiscountStack();
        }
    }

    /**
     * Validerar och applicerar manuell rabattkod (ny metod för multi-rabatt-systemet)
     */
    async validateAndApplyManualDiscount(code) {
        console.log('DiscountManager: Validating and applying manual discount:', code);
        
        if (this.manualDiscount && this.manualDiscount.code === code) {
            console.log('DiscountManager: Manual discount already applied');
            return;
        }
        
        try {
            // Validera rabattkoden
            const cartData = this.getCartValidationData();
            const merchantId = this.system.config.checkout?.merchantId || this.config.merchantId;
            const customerEmail = this.getCustomerEmail();
            
            const requestData = {
                code: code,
                merchantId: merchantId,
                customerId: this.config.customerId || this.system.config.customerId || null,
                customerEmail: customerEmail,
                orderAmount: cartData.totalAmount,
                productCategoryIds: cartData.categoryIds,
                productBrandIds: cartData.brandIds,
                categoryQuantities: cartData.categoryQuantities,
                productPrices: cartData.productPrices
            };
            
            console.log('DiscountManager: Sending validation request with product prices:', requestData);
            
            const response = await fetch(`${this.config.apiUrl}/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });
            
            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.isValid) {
                // Sätt manuell rabatt
                this.manualDiscount = {
                    id: result.discount?.id,
                    code: code,
                    name: result.description,
                    discountAmount: result.discountAmount,
                    description: result.description,
                    isAutomatic: false,
                    priority: 10 // Manuella rabatter har lägre prioritet
                };
                
                // Uppdatera rabatt-stacken
                this.updateDiscountStack();
                
                // Dispatcha event för manuell rabatt
                const event = new CustomEvent('manualDiscountApplied', {
                    detail: {
                        discount: this.manualDiscount,
                        amount: this.manualDiscount.discountAmount
                    }
                });
                document.dispatchEvent(event);
                
                this.showSuccess(t('discount.applied'));
                
            } else {
                this.showError(result.errorMessage || t('discount.invalid'));
            }
            
        } catch (error) {
            console.error('DiscountManager: Error validating manual discount:', error);
            this.showError(t('discount.error'));
        }
    }

    /**
     * Tar bort manuell rabatt (ny metod för multi-rabatt-systemet)
     */
    removeManualDiscount() {
        console.log('DiscountManager: Removing manual discount');
        
        if (this.manualDiscount) {
            this.manualDiscount = null;
            
            // Rensa input-fält
            if (this.elements.discountInput) {
                this.elements.discountInput.value = '';
            }
            
            // Uppdatera rabatt-stacken
            this.updateDiscountStack();
            
            // Dispatcha event
            const event = new CustomEvent('discountCodeRemoved', {
                detail: { type: 'manual' }
            });
            document.dispatchEvent(event);
            
            this.showSuccess(t('discount.removed'));
        }
    }

    /**
     * Uppdaterar rabatt-stacken baserat på automatiska och manuella rabatter
     */
    updateDiscountStack() {
        console.log('DiscountManager: Updating discount stack');
        
        // Bygg ny rabatt-stack
        this.discountStack = [];
        
        // Lägg till automatiska rabatter (högst prioritet)
        this.automaticDiscounts.forEach(discount => {
            this.discountStack.push(discount);
        });
        
        // Lägg till manuell rabatt (lägre prioritet)
        if (this.manualDiscount) {
            this.discountStack.push(this.manualDiscount);
        }
        
        // Sortera efter prioritet (lägre nummer = högre prioritet)
        this.discountStack.sort((a, b) => a.priority - b.priority);
        
        console.log('DiscountManager: Updated discount stack:', this.discountStack);
        
        // Dispatcha stack-uppdatering event
        const event = new CustomEvent('discountStackUpdated', {
            detail: {
                discountStack: this.discountStack,
                automaticDiscounts: this.automaticDiscounts,
                manualDiscount: this.manualDiscount,
                totalDiscountAmount: this.getTotalDiscountAmount()
            }
        });
        document.dispatchEvent(event);
        
        // Uppdatera UI
        this.updateOrderSummary();
        this.updateUI();
    }


    /**
     * Hämtar total rabattbelopp från alla rabatter i stacken
     */
    getTotalDiscountAmount() {
        return this.discountStack.reduce((total, discount) => total + discount.discountAmount, 0);
    }

    /**
     * Skapar beskrivning för rabatt-stacken
     */
    getStackDescription() {
        if (this.discountStack.length === 0) return '';
        if (this.discountStack.length === 1) return this.discountStack[0].description;
        
        return `${this.discountStack.length} rabatter applicerade`;
    }

    /**
     * Renderar separata rabatt-rader i order-sammanfattningen
     */
    renderSeparateDiscountRows() {
        console.log('DiscountManager: Rendering separate discount rows');
        
        // Hitta eller skapa container för rabatt-rader
        let discountContainer = document.getElementById('discount-rows-container');
        if (!discountContainer) {
            // Skapa container om den inte finns
            discountContainer = document.createElement('div');
            discountContainer.id = 'discount-rows-container';
            discountContainer.className = 'discount-rows-container';
            
            // Hitta order-totals containern från HTML-strukturen
            const orderTotals = document.querySelector('.order-totals');
            if (orderTotals) {
                // Hitta subtotal-raden
                const subtotalRow = orderTotals.querySelector('.order-subtotal');
                if (subtotalRow) {
                    // Lägg till efter subtotal-raden
                    subtotalRow.insertAdjacentElement('afterend', discountContainer);
                } else {
                    // Fallback: lägg till som första barn i order-totals
                    orderTotals.insertBefore(discountContainer, orderTotals.firstChild);
                }
            } else {
                console.warn('DiscountManager: Could not find .order-totals container');
                return;
            }
        }
        
        // Rensa befintligt innehåll
        discountContainer.innerHTML = '';
        
        // Rendera automatiska rabatter
        this.automaticDiscounts.forEach(discount => {
            const discountRow = document.createElement('div');
            discountRow.className = 'order-subtotal discount-row automatic-discount';
            discountRow.innerHTML = `
                <span>${discount.name}</span>
                <span>-${formatPrice(discount.discountAmount)}</span>
            `;
            discountContainer.appendChild(discountRow);
        });
        
        // Rendera manuell rabatt
        if (this.manualDiscount) {
            const discountRow = document.createElement('div');
            discountRow.className = 'order-subtotal discount-row manual-discount';
            discountRow.innerHTML = `
                <span>Rabatt: ${this.manualDiscount.code}</span>
                <span>-${formatPrice(this.manualDiscount.discountAmount)}</span>
            `;
            discountContainer.appendChild(discountRow);
        }
        
        // Dölj gamla discount-row om den finns
        if (this.elements.discountRow) {
            this.elements.discountRow.style.display = 'none';
        }
        
        console.log('DiscountManager: Rendered discount rows:', {
            automaticDiscounts: this.automaticDiscounts.length,
            manualDiscount: !!this.manualDiscount,
            containerExists: !!discountContainer
        });
    }

    /**
     * Renderar rabatt-lista (för framtida UI-förbättringar)
     */
    renderDiscountList() {
        console.log('DiscountManager: Rendering discount list');
        
        // Hitta eller skapa discount-list element
        let discountListElement = document.getElementById('discount-list');
        if (!discountListElement) {
            // Skapa discount-list om det inte finns
            discountListElement = document.createElement('div');
            discountListElement.id = 'discount-list';
            discountListElement.className = 'discount-list';
            
            // Lägg till efter discount-row om det finns
            const discountRow = this.elements.discountRow;
            if (discountRow && discountRow.parentNode) {
                discountRow.parentNode.insertBefore(discountListElement, discountRow.nextSibling);
            }
        }
        
        // Rensa befintligt innehåll
        discountListElement.innerHTML = '';
        
        if (this.discountStack.length === 0) {
            discountListElement.style.display = 'none';
            return;
        }
        
        // Visa discount-list
        discountListElement.style.display = 'block';
        
        // Rendera varje rabatt
        this.discountStack.forEach(discount => {
            const discountItem = document.createElement('div');
            discountItem.className = `discount-item ${discount.isAutomatic ? 'automatic' : 'manual'}`;
            
            discountItem.innerHTML = `
                <span class="discount-name">${discount.name}</span>
                <span class="discount-amount">-${formatPrice(discount.discountAmount)}</span>
                ${!discount.isAutomatic ? '<button class="remove-discount-item" data-code="' + discount.code + '">×</button>' : ''}
            `;
            
            discountListElement.appendChild(discountItem);
        });
        
        // Lägg till total-rad om flera rabatter
        if (this.discountStack.length > 1) {
            const totalItem = document.createElement('div');
            totalItem.className = 'discount-total';
            totalItem.innerHTML = `
                <strong>Totala rabatter: -${formatPrice(this.getTotalDiscountAmount())}</strong>
            `;
            discountListElement.appendChild(totalItem);
        }
        
        // Bind events för remove-knappar
        discountListElement.querySelectorAll('.remove-discount-item').forEach(button => {
            button.addEventListener('click', (e) => {
                const code = e.target.getAttribute('data-code');
                if (this.manualDiscount && this.manualDiscount.code === code) {
                    this.removeManualDiscount();
                }
            });
        });
    }

    // Public API methods
    getCurrentDiscount() {
        return this.currentDiscount;
    }

    getDiscountAmount() {
        return this.currentDiscount?.discountAmount || 0;
    }

    hasDiscount() {
        return this.currentDiscount !== null;
    }

    getDiscountData() {
        if (!this.currentDiscount) return null;
        
        return {
            code: this.currentDiscount.code,
            discountId: this.currentDiscount.discount?.id,
            discountAmount: this.currentDiscount.discountAmount,
            description: this.currentDiscount.description,
            isAutomatic: this.currentDiscount.isAutomatic || false
        };
    }

    /**
     * Hämtar nuvarande valt leveransalternativ från ShippingOptionsManager
     */
    getSelectedShippingOption() {
        const shippingManager = this.system.modules?.shippingOptions;
        if (shippingManager && typeof shippingManager.getSelectedOption === 'function') {
            return shippingManager.getSelectedOption();
        }
        
        // Fallback: försök hitta valt alternativ i DOM
        const selectedRadio = document.querySelector('input[name="shippingOption"]:checked');
        if (selectedRadio) {
            return {
                id: selectedRadio.value,
                name: selectedRadio.dataset.name,
                shippingMethod: selectedRadio.dataset.method,
                price: parseFloat(selectedRadio.dataset.price) || 0
            };
        }
        
        return null;
    }

    /**
     * Kontrollerar om en rabatt är tillämplig för en specifik leveransmetod
     */
    isDiscountApplicableForShippingMethod(discount, shippingOption) {
        console.log('DiscountManager: Checking if discount is applicable for shipping method:', {
            discountName: discount.name,
            applicableShippingMethods: discount.applicableShippingMethods,
            shippingOption: shippingOption
        });
        
        // Om ingen leveransmetod är vald än, anta att rabatten kan vara tillämplig
        if (!shippingOption || !shippingOption.shippingMethod) {
            console.log('DiscountManager: No shipping method selected, assuming discount is applicable');
            return true;
        }
        
        // Om inga specifika leveransmetoder är angivna, gäller rabatten för alla
        if (!discount.applicableShippingMethods || discount.applicableShippingMethods.length === 0) {
            console.log('DiscountManager: No specific shipping methods defined, discount applies to all');
            return true;
        }
        
        // Direkt jämförelse utan manuell mapping - frontend och backend använder samma ShippingMethod enum
        const isApplicable = discount.applicableShippingMethods.includes(shippingOption.shippingMethod);
        
        console.log('DiscountManager: Shipping method applicability check:', {
            shippingMethod: shippingOption.shippingMethod,
            applicableShippingMethods: discount.applicableShippingMethods,
            isApplicable: isApplicable
        });
        
        return isApplicable;
    }

    // Method for checkout integration - Clean multiple discounts format
    includeInOrder(orderData) {
        // Only use the new multiple discounts format
        if (this.discountStack && this.discountStack.length > 0) {
            console.log('DiscountManager: Including multiple discounts in order:', this.discountStack);
            
            orderData.discounts = this.discountStack.map(discount => ({
                code: discount.code,
                amount: Math.round(discount.discountAmount * 100), // Convert to öre
                description: discount.description || discount.name
            }));
            
            console.log('DiscountManager: Mapped discounts for order:', orderData.discounts);
        }
        
        return orderData;
    }
}
